import React, { useEffect, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { toast } from 'react-toastify';
import clinicBooking from '../../../../../services/clinicbooking.service';
import { useParams } from 'react-router-dom';
import DocInfo from './DocInfo';
import Loading from '../../../../common/Loading';
import { useForm } from 'react-hook-form';
import clinicService from '../../../../../services/clinic.service';
import clinicbookingService from '../../../../../services/clinicbooking.service';
// Add the AddDoctorInfo component here
function AddDoctorInfo({ onDoctorAdded }) {
    const [clinicDoctors, setClinicDoctors] = useState([]);
    const [existingBookingDoctors, setExistingBookingDoctors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { clinicId } = useParams();

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm();

    useEffect(() => {
        Promise.all([getClinicDoctors(), getExistingBookingDoctors()])
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const getClinicDoctors = () => {
        return clinicService.getUsers(clinicId, "ECP")
            .then((res) => {
                setClinicDoctors(res.data);
            })
            .catch((err) => {
                console.log(err);
                toast.error("Failed to load doctors", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            });
    };

    const getExistingBookingDoctors = () => {
        return clinicbookingService.getAllDoctors(clinicId)
            .then((res) => {
                setExistingBookingDoctors(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getAvailableDoctors = () => {
        const existingDoctorIds = existingBookingDoctors.map(doc => doc.userId);
        return clinicDoctors.filter(doc => !existingDoctorIds.includes(doc.id));
    };

    const onSubmit = (data) => {
        setIsSubmitting(true);
        const newData = { ...data, clinicId: clinicId };

        clinicbookingService.addUpdateDoctor(clinicId, newData)
            .then((res) => {
                toast.success("Doctor information saved successfully!", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });

                reset();
                getExistingBookingDoctors();

                if (onDoctorAdded) {
                    onDoctorAdded();
                }
            })
            .catch((err) => {
                console.log(err);
                toast.error(err.response?.data?.message || "Failed to save doctor information", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const availableDoctors = getAvailableDoctors();

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header">
                <i className="bi bi-person-plus"></i>
                Add New Doctor
            </div>
            <div className="notifications-panel__body">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label htmlFor="userId" className="notifications-form-label">
                                Select Doctor <span className="text-danger">*</span>
                            </label>
                            {isLoading ? (
                                <Loading />
                            ) : availableDoctors.length === 0 ? (
                                <div className="alert alert-warning py-2 mb-0 border-0">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    All doctors are already added to the booking system.
                                </div>
                            ) : (
                                <>
                                    <select
                                        id="userId"
                                        className={`form-select form-select-sm ${errors.userId ? 'is-invalid' : ''}`}
                                        {...register('userId', { required: 'Doctor selection is required' })}
                                    >
                                        <option value="">Select a doctor</option>
                                        {availableDoctors.map((doc) => (
                                            <option key={doc.id} value={doc.id}>
                                                Dr. {doc.firstName} {doc.lastName}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.userId && <div className="invalid-feedback">{errors.userId.message}</div>}
                                </>
                            )}
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="name" className="notifications-form-label">
                                Display Name <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                className={`form-control form-control-sm ${errors.name ? 'is-invalid' : ''}`}
                                {...register('name', { required: 'Display name is required' })}
                                placeholder="Enter display name"
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="photoUrl" className="notifications-form-label">
                                Photo URL
                            </label>
                            <input
                                type="text"
                                id="photoUrl"
                                className="form-control form-control-sm"
                                {...register('photoUrl')}
                                placeholder="https://example.com/photo.jpg"
                            />
                            <div className="notifications-form-hint">Leave blank to use default avatar</div>
                        </div>

                        <div className="col-md-6">
                            <label htmlFor="isActive" className="notifications-form-label">
                                Status <span className="text-danger">*</span>
                            </label>
                            <select
                                id="isActive"
                                className={`form-select form-select-sm ${errors.isActive ? 'is-invalid' : ''}`}
                                {...register('isActive', { required: 'Status is required' })}
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                            {errors.isActive && <div className="invalid-feedback">{errors.isActive.message}</div>}
                        </div>

                        <div className="col-12">
                            <label htmlFor="bio" className="notifications-form-label">
                                Biography
                            </label>
                            <textarea
                                id="bio"
                                className="form-control form-control-sm"
                                rows={4}
                                {...register('bio')}
                                placeholder="Enter doctor's biography (education, specialties, etc.)"
                            ></textarea>
                        </div>
                    </div>

                    <div className="clinic-booking-form-actions">
                        <button
                            type="submit"
                            className="btn btn-success btn-sm"
                            disabled={isSubmitting || availableDoctors.length === 0}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-floppy me-1"></i>
                                    Save Doctor
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Main AllDocs component
function AllDocs({ refresh = false }) {
    const [docList, setDocList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDoc, setSelectedDoc] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [showAddForm, setShowAddForm] = useState(false);
    const { clinicId } = useParams();

    useEffect(() => {
        getAllDoctors();
    }, [refresh, showAddForm]); // Add showAddForm to dependency array

    const getAllDoctors = () => {
        setIsLoading(true);
        clinicBooking.getAllDoctors(clinicId)
            .then(res => {
                setDocList(res.data);
            })
            .catch(err => {
                console.error(err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleEdit = (doc) => {
        setSelectedDoc(doc);
        setIsEditing(true);
    };

    const handleBackToList = () => {
        setIsEditing(false);
        getAllDoctors();
    };

    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const toggleAddForm = () => {
        setShowAddForm(!showAddForm);
    };

    const handleDoctorAdded = () => {
        setShowAddForm(false);
        getAllDoctors();
    };

    // Calculate stats for the dashboard
    const activeDoctors = docList.filter(doc => doc.isActive).length;
    const inactiveDoctors = docList.filter(doc => !doc.isActive).length;
    const totalDoctors = docList.length;

    if (isEditing) {
        return <DocInfo doc={selectedDoc} onBack={handleBackToList} />;
    }

    return (
        <>
            <div className="clinic-booking-toolbar">
                <div className="d-flex flex-wrap gap-2">
                    <span className="clinic-booking-stat clinic-booking-stat--active">Active: {activeDoctors}</span>
                    <span className="clinic-booking-stat clinic-booking-stat--inactive">Inactive: {inactiveDoctors}</span>
                    <span className="clinic-booking-stat clinic-booking-stat--total">Total: {totalDoctors}</span>
                </div>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={toggleAddForm}>
                    <i className={`bi ${showAddForm ? 'bi-x-lg' : 'bi-plus-circle'} me-1`}></i>
                    {showAddForm ? 'Cancel' : 'Add Doctor'}
                </button>
            </div>

            <Collapse in={showAddForm}>
                <div className="mb-3">
                    <AddDoctorInfo onDoctorAdded={handleDoctorAdded} />
                </div>
            </Collapse>

            {isLoading ? (
                <Loading />
            ) : docList.length > 0 ? (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-booking-table">
                        <thead>
                            <tr>
                                <th>
                                    <i className="bi bi-person-badge"></i> Doctor
                                </th>
                                <th>
                                    <i className="bi bi-calendar-plus"></i> Joined
                                </th>
                                <th>
                                    <i className="bi bi-toggle-on"></i> Status
                                </th>
                                <th className="text-end">
                                    <i className="bi bi-gear"></i> Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {docList.map((doc) => (
                                <React.Fragment key={doc.id}>
                                    <tr className="clinic-booking-row">
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                {doc.photoUrl ? (
                                                    <img
                                                        src={doc.photoUrl}
                                                        alt={doc.name}
                                                        className="clinic-booking-avatar"
                                                    />
                                                ) : (
                                                    <div className="clinic-booking-avatar clinic-booking-avatar--placeholder">
                                                        {doc.name.charAt(0)}
                                                    </div>
                                                )}
                                                <div className="min-w-0">
                                                    <div className="clinic-booking-name">{doc.name}</div>
                                                    {doc.email && <div className="clinic-booking-meta">{doc.email}</div>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="clinic-booking-meta">{doc.createdAt}</td>
                                        <td>
                                            <span
                                                className={`clinic-booking-status-badge ${
                                                    doc.isActive
                                                        ? 'clinic-booking-status-badge--active'
                                                        : 'clinic-booking-status-badge--inactive'
                                                }`}
                                            >
                                                {doc.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="text-end">
                                            <div className="d-flex justify-content-end gap-1">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => handleEdit(doc)}
                                                    title="Edit doctor"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => toggleRowExpansion(doc.id)}
                                                    title="View bio"
                                                >
                                                    <i
                                                        className={`bi ${expandedRows[doc.id] ? 'bi-chevron-up' : 'bi-chevron-down'}`}
                                                    ></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedRows[doc.id] && (
                                        <tr>
                                            <td colSpan="4" className="p-0 border-0">
                                                <div className="clinic-booking-detail-panel">
                                                    <div className="notifications-info-label mb-1">Biography</div>
                                                    <div className="notifications-info-value">
                                                        {doc.bio || 'No biography available for this doctor.'}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="clinic-booking-empty">
                    <i className="bi bi-person-plus"></i>
                    No doctors found.
                    <div className="mt-3">
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={toggleAddForm}>
                            <i className="bi bi-plus-circle me-1"></i>
                            Add First Doctor
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default AllDocs;